<template>
  <div class="tx-app">
    <!-- Top bar -->
    <div class="tx-topbar">
      <div class="d-flex align-items-baseline">
        <h1 class="tx-title">Treatment Presentations</h1>
        <span class="tx-title-date">{{ todayLabel }}</span>
      </div>
      <div class="ml-auto d-flex align-items-center" style="gap: 12px;">
        <div class="tx-search">
          <i class="fa tx-search-icon" :class="searching ? 'fa-spinner fa-spin' : 'fa-search'"></i>
          <input
            v-model="searchInput"
            class="tx-search-input"
            placeholder="Search patient name"
            @input="onSearchInput"
            @keyup.enter="onSearchEnter"
          />
        </div>
        <button class="tx-btn" @click="onRefresh" :disabled="loading">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
        <button class="tx-btn" @click="onOpenSettings" title="Tx Presenter Settings">
          <i class="fa fa-cog"></i>
          Settings
        </button>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="tx-filterbar">
      <!-- Active date-range window (bold) — replaces the old "All N" tab. -->
      <div class="tx-daterange" v-if="fromDate && toDate">
        {{ formatDate(fromDate) }} – {{ formatDate(toDate) }}
      </div>
      <div class="tx-daterange tx-daterange-search" v-else>Search results</div>
      <div class="ml-auto d-flex align-items-center" style="gap: 8px;">
        <multiselect
          v-if="providers"
          v-model="selectedProviders"
          class="tx-provider-multiselect"
          :options="providers"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :show-labels="false"
          :custom-label="providerCustomLabel"
          :limit="1"
          :limit-text="(count) => `+${count} more`"
          label="FName"
          track-by="ProvNum"
          placeholder="All providers"
          @input="onProviderChange"
        />
        <div class="tx-window-select">
          <i class="fa fa-calendar tx-window-icon"></i>
          <select v-model="windowDaysInput" @change="onFilterChange" class="tx-window-input">
            <option :value="7">Last 7 days</option>
            <option :value="30">Last 30 days</option>
            <option :value="60">Last 60 days</option>
            <option :value="90">Last 90 days</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Error banner -->
    <div v-if="error" class="tx-error-banner">
      {{ error }}
      <button class="tx-icon-btn ml-auto" @click="clearError">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <!-- Layout: table + drawer -->
    <div class="tx-layout">
      <!-- Table -->
      <div class="tx-table-wrapper">
        <div class="tx-table-card">
          <div class="tx-table-scroll tx-scroll">
            <table class="tx-table">
              <thead>
                <tr>
                  <th class="tx-th-patient">Patient</th>
                  <th>Provider</th>
                  <th>Treatment Plan</th>
                  <th class="text-right">Total</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in patients"
                  :key="row.patNum"
                  class="tx-row"
                  :class="{ 'tx-row-selected': drawer.patient && drawer.patient.patNum === row.patNum }"
                  @click="onRowClick(row)"
                >
                  <td>
                    <div class="d-flex align-items-center" style="gap: 10px;">
                      <div class="tx-patient-avatar" :style="patientAvatarStyle(row)">
                        {{ patientInitials(row) }}
                      </div>
                      <div style="min-width: 0;">
                        <div class="tx-patient-name">
                          {{ row.lName }}, {{ row.fName }}
                          <!-- Slice 8e — Comments / Q&A items demanding TC
                               attention. Counts patient questions + AI drafts
                               held for review; excludes auto-released AI
                               replies (those are derivative of the patient
                               question already counted). Drains when the TC
                               opens the Builder for this patient
                               (markThreadReadByOffice fires on poll/load). -->
                          <span
                            v-if="row.unreadCommentCount > 0"
                            class="tx-unread-chip"
                            :title="`${row.unreadCommentCount} pending update${row.unreadCommentCount === 1 ? '' : 's'} on this patient's plan`"
                          >
                            <i class="fa fa-comments"></i>
                            {{ row.unreadCommentCount }} new
                          </span>
                        </div>
                        <div class="tx-patient-sub">
                          DOB {{ formatDate(row.birthdate) }}
                          <span v-if="row.chartNumber"> · {{ row.chartNumber }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="tx-cell-provider">
                    {{ row.provider || '—' }}
                  </td>
                  <td>
                    <div class="tx-plan-heading">
                      {{ row.activeHeading || '—' }}
                      <span v-if="row.savedPlanCount > 0" class="tx-saved-chip" :title="`${row.savedPlanCount} saved plan(s)`">
                        +{{ row.savedPlanCount }} saved
                      </span>
                    </div>
                    <div v-if="row.nextVisit" class="tx-plan-sub">
                      <i class="fa fa-calendar-check-o"></i>
                      Next visit {{ formatDateTime(row.nextVisit) }}
                    </div>
                  </td>
                  <td class="text-right tx-tab-num tx-cell-total">
                    {{ formatMoney(row.activeTotal) }}
                  </td>
                  <td class="tx-cell-updated">
                    {{ formatRel(row.lastTPActivity) }}
                  </td>
                </tr>
                <tr v-if="patients.length === 0 && !loading">
                  <td colspan="5" class="text-center" style="padding: 40px; color: var(--tx-text-dim);">
                    No patients with treatment-planned procedures in this time window.
                  </td>
                </tr>
                <tr v-if="loading && patients.length === 0">
                  <td colspan="5" class="text-center" style="padding: 40px;">
                    <i class="fa fa-spinner fa-spin"></i> Loading patients…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="tx-table-footer">
            <span>Showing {{ patients.length }} of {{ totalCount }}</span>
            <span v-if="fromDate && toDate" class="tx-table-footer-sub">
              · {{ formatDate(fromDate) }} to {{ formatDate(toDate) }}
            </span>
            <span v-if="loading" class="tx-table-footer-loading">
              <i class="fa fa-spinner fa-spin"></i> Loading…
            </span>
          </div>
        </div>
      </div>

      <!-- Drawer -->
      <transition name="tx-drawer-slide">
        <TxPresenterDrawer v-if="drawer.open" />
      </transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import mixin from '@/mixin.js';
import TxPresenterDrawer from './components/TxPresenterDrawer.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { debounce } from 'lodash';

export default {
  name: 'TxPresenterDashboard',
  mixins: [mixin],
  components: { TxPresenterDrawer, Multiselect },

  data() {
    return {
      searchInput: '',
      searching: false,   // in-box spinner while a live search is in flight
      windowDaysInput: 30,
      selectedProviders: [],
    };
  },

  computed: {
    ...mapState('txPresenter', {
      dashboardState: (state) => state.dashboard,
      drawer: (state) => state.drawer,
      filters: (state) => state.filters,
    }),
    ...mapState('customer', ['providers']),

    patients() { return this.dashboardState.patients; },
    totalCount() { return this.dashboardState.totalCount; },
    fromDate() { return this.dashboardState.fromDate; },
    toDate() { return this.dashboardState.toDate; },
    loading() { return this.dashboardState.loading; },
    error() { return this.dashboardState.error; },

    todayLabel() {
      return moment().format('MMM D, YYYY');
    },
  },

  watch: {
    siteID: {
      immediate: true,
      handler(newSite) {
        if (newSite) this.loadDashboard();
      },
    },
  },

  created() {
    // Live search: debounce keystrokes so typing fires one server query per
    // pause, not per character (search is server-side). Enter / clearing the
    // box bypass the debounce (see onSearchEnter / onSearchInput).
    this.debouncedSearch = debounce(this.onSearch, 300);
  },

  beforeDestroy() {
    if (this.debouncedSearch) this.debouncedSearch.cancel();
  },

  mounted() {
    this.windowDaysInput = this.filters.windowDays;
    this.searchInput = this.filters.search || '';
    // Reflect any persisted provider filter in the multiselect on (re)mount.
    const provNums = this.filters.providerNums || [];
    if (provNums.length && this.providers) {
      this.selectedProviders = this.providers.filter((p) => provNums.includes(p.ProvNum));
    }
  },

  methods: {
    ...mapActions('txPresenter', ['fetchDashboard', 'fetchPresentationDetail', 'updateFilter']),

    async loadDashboard() {
      if (!this.siteID) return;
      try {
        await this.fetchDashboard({ siteID: this.siteID });
      } catch (e) { /* error in state */ }
    },

    onRefresh() { this.loadDashboard(); },

    onOpenSettings() {
      this.$router.push({ name: 'TxPresenterSettings' });
    },

    // Core runner — re-fetches the dashboard for the current query (server-side
    // search; reads searchInput at call time so the debounced run uses the
    // latest value). No-op when unchanged. `searching` drives the in-box
    // spinner; the store guards against out-of-order responses (latest-wins).
    async onSearch() {
      const trimmed = (this.searchInput || '').trim();
      if (trimmed === (this.filters.search || '')) return;
      this.searching = true;
      try {
        await this.updateFilter({ key: 'search', value: trimmed });
      } finally {
        this.searching = false;
      }
    },

    // Per-keystroke entry point. Debounced while typing; instant-on-clear so
    // erasing the box snaps straight back to the date-windowed list with no wait.
    onSearchInput() {
      if ((this.searchInput || '').trim() === '') {
        this.debouncedSearch.cancel();
        this.onSearch();
        return;
      }
      this.debouncedSearch();
    },

    // Enter searches immediately (flush any pending debounce).
    onSearchEnter() {
      this.debouncedSearch.cancel();
      this.onSearch();
    },

    async onFilterChange() {
      await this.updateFilter({ key: 'windowDays', value: this.windowDaysInput });
    },

    providerCustomLabel({ FName, MI, LName, Suffix, Abbr }) {
      return `${FName || ''} ${MI || ''} ${LName || ''} ${Suffix || ''} (${Abbr || ''})`
        .replace(/\s+/g, ' ')
        .trim();
    },

    // vue-multiselect emits the new selection array on @input (alongside v-model),
    // so read the payload directly to avoid a stale-`this.selectedProviders` race.
    async onProviderChange(selected) {
      await this.updateFilter({
        key: 'providerNums',
        value: (selected || []).map((p) => p.ProvNum),
      });
    },

    async onRowClick(item) {
      if (!item || !item.patNum) return;
      try {
        await this.fetchPresentationDetail({
          siteID: this.siteID,
          patNum: item.patNum,
        });
      } catch (e) { /* error in state */ }
    },

    clearError() {
      this.$store.commit('txPresenter/SET_DASHBOARD_ERROR', null);
    },

    // Avatar helpers
    patientInitials(row) {
      const f = (row.fName || '').charAt(0);
      const l = (row.lName || '').charAt(0);
      return (f + l).toUpperCase() || '??';
    },
    patientAvatarStyle(row) {
      // Deterministic color from initials
      const init = this.patientInitials(row);
      const hash = (init.charCodeAt(0) || 0) + (init.charCodeAt(1) || 0);
      const hues = [186, 214, 262, 28, 170, 330, 42, 200, 280];
      const h = hues[hash % hues.length];
      return { background: `oklch(0.68 0.11 ${h})` };
    },

    formatDate(d) {
      if (!d || d === '0001-01-01') return '—';
      return moment(d).format('MMM D, YYYY');
    },
    formatDateTime(d) {
      if (!d) return '—';
      return moment(d).format('MMM D · h:mm A');
    },
    formatRel(d) {
      if (!d || d === '0001-01-01') return '—';
      const m = moment(d);
      const diff = moment().diff(m, 'days');
      if (diff === 0) return 'Today';
      if (diff === 1) return 'Yesterday';
      if (diff < 7) return `${diff}d ago`;
      if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
      return m.format('MMM D, YYYY');
    },
    formatMoney(n) {
      if (n === null || n === undefined) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
/* Scoped tokens — locally-applied design system from Claude Design package */
.tx-app {
  --tx-bg: #f7f8fa;
  --tx-surface: #ffffff;
  --tx-surface2: #f1f3f6;
  --tx-border: #e4e7ec;
  --tx-border-strong: #cdd3dc;
  --tx-text: #0f172a;
  --tx-text-mute: #475569;
  --tx-text-dim: #94a3b8;
  --tx-accent: #0f6e63;
  --tx-accent-soft: #e6f1ef;
  --tx-accent-ink: #094840;
  --tx-shadow-sm: 0 1px 2px rgba(15,23,42,0.04);
  --tx-shadow-md: 0 2px 6px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: var(--tx-text);
  font-size: 14px;
  line-height: 1.45;
  background: var(--tx-bg);
  min-height: calc(100vh - 80px);
  -webkit-font-smoothing: antialiased;
}

.tx-app * { box-sizing: border-box; }
.tx-app >>> .fa { font-size: inherit; }
.tx-tab-num { font-variant-numeric: tabular-nums; }

/* ─── Top bar ─── */
.tx-topbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px 14px;
  background: var(--tx-surface);
  border-bottom: 1px solid var(--tx-border);
}
.tx-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.3px;
}
.tx-title-date {
  font-size: 13px;
  color: var(--tx-text-dim);
  margin-left: 12px;
}
.tx-search {
  position: relative;
  width: 280px;
}
.tx-search-icon {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: var(--tx-text-dim);
  font-size: 13px;
}
.tx-search-input {
  width: 100%;
  padding: 7px 10px 7px 30px;
  border-radius: 7px;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  font-size: 13px;
  outline: none;
  transition: border-color .12s, box-shadow .12s;
  color: var(--tx-text);
}
.tx-search-input:focus {
  border-color: var(--tx-accent);
  box-shadow: 0 0 0 3px var(--tx-accent-soft);
}
.tx-search-input::placeholder { color: var(--tx-text-dim); }

/* ─── Buttons ─── */
.tx-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--tx-border);
  background: var(--tx-surface);
  color: var(--tx-text);
  cursor: pointer;
  transition: all .12s;
}
.tx-btn:hover:not(:disabled) {
  background: var(--tx-surface2);
  border-color: var(--tx-border-strong);
}
.tx-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.tx-btn-suffix { color: var(--tx-text-dim); font-size: 11px; }
.tx-icon-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--tx-text-mute);
  cursor: pointer;
  transition: all .12s;
}
.tx-icon-btn:hover { background: var(--tx-surface2); color: var(--tx-text); }

/* ─── Filter bar ─── */
.tx-filterbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 24px;
}
.tx-tabs {
  display: flex;
  gap: 2px;
  background: var(--tx-surface);
  border: 1px solid var(--tx-border);
  border-radius: 8px;
  padding: 3px;
}
.tx-tab {
  padding: 5px 11px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--tx-text-mute);
  font-weight: 450;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all .12s;
}
.tx-tab.active {
  background: var(--tx-surface2);
  color: var(--tx-text);
  font-weight: 550;
}
.tx-tab-count {
  font-size: 11px;
  font-weight: 500;
  background: transparent;
  color: var(--tx-text-dim);
  padding: 0 5px;
  border-radius: 8px;
}
.tx-tab.active .tx-tab-count { background: #fff; }

.tx-window-select {
  position: relative;
}
.tx-window-icon {
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: var(--tx-text-dim);
  font-size: 13px;
  pointer-events: none;
}
.tx-window-input {
  /* Native <select> rendered the selected value clipped/invisible because the
     vendor appearance resets + chevron were missing. Mirror the canonical
     select.sg-input fix: reset appearance on all engines + draw our own chevron
     so the selected option always shows. Left padding clears the calendar icon. */
  height: 34px;
  line-height: 1.4;
  padding: 0 30px 0 30px;
  border-radius: 7px;
  border: 1px solid var(--tx-border);
  background-color: var(--tx-surface);
  font-size: 13px;
  outline: none;
  color: var(--tx-text);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%23475569' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M3 4.5l3 3 3-3'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 12px;
  cursor: pointer;
}

/* Active date-range window label (bold) — replaces the old "All N" tab. */
.tx-daterange {
  font-weight: 700;
  font-size: 14px;
  color: var(--tx-text);
  letter-spacing: 0.01em;
}
.tx-daterange-search {
  font-weight: 600;
  color: var(--tx-text-dim);
}

/* Provider filter (vue-multiselect) — keep it compact in the filter bar. */
.tx-provider-multiselect {
  min-width: 210px;
  max-width: 280px;
  font-size: 13px;
}

.tx-error-banner {
  display: flex;
  align-items: center;
  margin: 0 24px 14px;
  padding: 10px 14px;
  background: #fbe0e4;
  color: #be123c;
  border-radius: 8px;
  font-size: 13px;
}

/* ─── Layout ─── */
.tx-layout {
  display: flex;
  gap: 16px;
  padding: 0 24px 24px;
  align-items: flex-start;
}
.tx-table-wrapper {
  flex: 1;
  min-width: 0;
}
.tx-table-card {
  background: var(--tx-surface);
  border: 1px solid var(--tx-border);
  border-radius: 10px;
  overflow: hidden;
}
.tx-table-scroll {
  overflow: auto;
  max-height: calc(100vh - 270px);
}

/* ─── Table ─── */
.tx-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tx-table thead th {
  position: sticky;
  top: 0;
  background: var(--tx-surface);
  font-size: 11px;
  font-weight: 550;
  color: var(--tx-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid var(--tx-border);
  z-index: 1;
}
.tx-table thead th.text-right { text-align: right; }
.tx-table thead th.tx-th-overflow { width: 44px; }
.tx-table thead th.tx-th-patient { padding-left: 16px; }

.tx-row {
  cursor: pointer;
  transition: background .12s;
}
.tx-row:hover { background: #f7f9fc; }
.tx-row-selected { background: var(--tx-accent-soft) !important; }

.tx-table tbody td {
  padding: 12px 12px;
  border-bottom: 1px solid var(--tx-border);
  vertical-align: middle;
}
.tx-table tbody td:first-child { padding-left: 16px; }

.tx-patient-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -0.2px;
}
.tx-patient-name {
  font-weight: 550;
  color: var(--tx-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tx-patient-sub {
  font-size: 11px;
  color: var(--tx-text-dim);
  margin-top: 2px;
}

.tx-plan-heading {
  color: var(--tx-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 340px;
}
.tx-saved-chip {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 7px;
  background: var(--tx-surface2);
  color: var(--tx-text-mute);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}
/* Slice 8e — unread Comments / Q&A pill on dashboard rows.
   Amber tint to distinguish "needs attention" from the neutral saved-plan
   chip. Subtle pulse pulls the eye without screaming. */
.tx-unread-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 8px;
  padding: 1px 8px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #f1d8b1;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  vertical-align: 1px;
  animation: tx-unread-chip-pulse 2.4s ease-in-out infinite;
}
.tx-unread-chip .fa { font-size: 10px; color: #b45309; }
@keyframes tx-unread-chip-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.35); }
  50%      { box-shadow: 0 0 0 4px rgba(245, 158, 11, 0);    }
}
.tx-plan-sub {
  font-size: 11px;
  color: var(--tx-text-dim);
  margin-top: 2px;
}
.tx-plan-sub i { margin-right: 4px; }

.tx-cell-total {
  font-weight: 550;
}
.tx-cell-presenter {
  color: var(--tx-text-mute);
  font-size: 12px;
}
.tx-cell-updated {
  color: var(--tx-text-mute);
  font-size: 12px;
}

.tx-table-footer {
  padding: 8px 16px;
  border-top: 1px solid var(--tx-border);
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--tx-text-mute);
  gap: 6px;
}
.tx-table-footer-sub { color: var(--tx-text-dim); }
.tx-table-footer-loading { margin-left: auto; color: var(--tx-text-dim); }

/* ─── Drawer transition ─── */
.tx-drawer-slide-enter-active, .tx-drawer-slide-leave-active {
  transition: all .2s ease;
}
.tx-drawer-slide-enter, .tx-drawer-slide-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

/* ─── Tabular nums + scroll ─── */
.tx-table-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.tx-table-scroll::-webkit-scrollbar-track { background: transparent; }
.tx-table-scroll::-webkit-scrollbar-thumb {
  background: #cbd3de;
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: content-box;
}
.tx-table-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
  background-clip: content-box;
}
</style>

<style>
/* Global font import (unscoped so the Inter font is available) */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;450;500;550;600;700&display=swap');
</style>
